/**
 * LegalForm Component
 *
 * Form for updating legal information.
 */

import { useState } from 'react';
import { Scale, Building2, Hash } from 'lucide-react';
import { Button } from '@elbklar/ui';
import { TextareaField, TextField } from '@/components/form/Felder';
import { useUpdateLegal } from '@/hooks/useTenantConfig';

interface TenantConfig {
  legal_name: string | null;
  vat_id: string | null;
  tax_number: string | null;
  registry_court: string | null;
  registry_number: string | null;
  managing_directors: string | null;
}

interface LegalFormProps {
  config: TenantConfig;
}

export function LegalForm({ config }: LegalFormProps) {
  const [legalName, setLegalName] = useState(config.legal_name || '');
  const [vatId, setVatId] = useState(config.vat_id || '');
  const [taxNumber, setTaxNumber] = useState(config.tax_number || '');
  const [registryCourt, setRegistryCourt] = useState(config.registry_court || '');
  const [registryNumber, setRegistryNumber] = useState(config.registry_number || '');
  const [managingDirectors, setManagingDirectors] = useState(config.managing_directors || '');

  const updateMutation = useUpdateLegal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await updateMutation.mutateAsync({
      legal_name: legalName || undefined,
      vat_id: vatId || undefined,
      tax_number: taxNumber || undefined,
      registry_court: registryCourt || undefined,
      registry_number: registryNumber || undefined,
      managing_directors: managingDirectors || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      {/* Legal Name */}
      <TextField label="Vollständiger Firmenname (Rechtsform)" icon={Building2} type="text" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Elbklar GmbH" />

      {/* Tax IDs */}
      <div className="border-t border-border pt-4">
        <h3 className="mb-4 flex items-center gap-2 t-title">
          <Scale size={18} strokeWidth={1.75} className="text-highlight" aria-hidden />
          Steuerliche Angaben
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* VAT ID */}
          <TextField label="USt-IdNr." icon={Hash} type="text" value={vatId} onChange={(e) => setVatId(e.target.value)} placeholder="DE123456789" />

          {/* Tax Number */}
          <TextField label="Steuernummer" type="text" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} placeholder="12/345/67890" />
        </div>
      </div>

      {/* Registry Info */}
      <div className="border-t border-border pt-4">
        <h3 className="mb-4 t-title">Handelsregister</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Registry Court */}
          <TextField label="Amtsgericht" type="text" value={registryCourt} onChange={(e) => setRegistryCourt(e.target.value)} placeholder="Dresden" />

          {/* Registry Number */}
          <TextField label="Handelsregister-Nr." type="text" value={registryNumber} onChange={(e) => setRegistryNumber(e.target.value)} placeholder="HRB 12345" />
        </div>
      </div>

      {/* Managing Directors */}
      <div className="border-t border-border pt-4">
        <TextareaField
          label="Geschäftsführung"
          value={managingDirectors}
          onChange={(e) => setManagingDirectors(e.target.value)}
          rows={2}
          placeholder="Max Mustermann, Erika Musterfrau"
          hint="Mehrere Personen durch Komma getrennt"
        />
      </div>

      {/* Submit */}
      <div className="flex items-center gap-4 border-t border-border pt-4">
        <Button type="submit" variant="primary" loading={updateMutation.isPending}>
          Speichern
        </Button>
        {updateMutation.isSuccess && <span className="t-small text-success">Gespeichert</span>}
        {updateMutation.isError && (
          <span className="t-small text-danger">{(updateMutation.error as Error)?.message || 'Speichern fehlgeschlagen'}</span>
        )}
      </div>
    </form>
  );
}

export default LegalForm;
